/**
 * Soft, dismissible "verify your email" banner.
 *
 * Shown in Layout for authenticated users who signed up with email+password
 * and haven't yet clicked the Firebase verification link. Does NOT block access
 * to anything — just a friendly nudge with a Resend button.
 *
 * Behavior:
 * - Hidden if user signed in with Google (providerId === 'google.com'): OAuth
 *   already verifies the email, so there's nothing to verify.
 * - Hidden once emailVerified === true.
 * - Dismissible per-session via sessionStorage key. Reappears on next login/session
 *   so users don't forget forever.
 * - "Resend email" calls AuthContext.sendVerification() and shows a quick toast-like
 *   confirmation state for ~3 seconds.
 * - "I've verified" calls auth.currentUser.reload() and re-reads emailVerified.
 */

import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, IconButton, useTheme } from '@mui/material';
import {
  MarkEmailRead as MarkEmailReadIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { iosColors, alphaHex } from './CarePlan/iosTabs/shared';
import { logger } from '../services/logger';

const log = logger.createScope('EmailVerificationBanner');

const DISMISS_KEY = 'suprie.emailVerifyBannerDismissed';

const EmailVerificationBanner: React.FC = () => {
  const { currentUser, sendVerification } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [checking, setChecking] = useState(false);
  const [, forceRender] = useState(0);

  // Detect sign-up method. Only password signups need verification — Google/Apple
  // OAuth users are already verified by the provider.
  const isPasswordSignup = useMemo(() => {
    if (!currentUser) return false;
    return currentUser.providerData.some((p) => p.providerId === 'password');
  }, [currentUser]);

  if (!currentUser) return null;
  if (currentUser.emailVerified) return null;
  if (!isPasswordSignup) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // sessionStorage unavailable (Safari private mode etc.) — just dismiss in-memory
    }
    setDismissed(true);
  };

  const handleResend = async () => {
    setResendState('sending');
    try {
      await sendVerification();
      setResendState('sent');
      setTimeout(() => setResendState('idle'), 3000);
    } catch (err) {
      log.error('Failed to resend verification email', err as Error);
      setResendState('error');
      setTimeout(() => setResendState('idle'), 3000);
    }
  };

  const handleCheck = async () => {
    setChecking(true);
    try {
      const auth = getAuth();
      await auth.currentUser?.reload();
      // reload() mutates the user object in place — poke React to re-render so we
      // pick up the new emailVerified value.
      forceRender((n) => n + 1);
    } catch (err) {
      log.error('Failed to reload user', err as Error);
    } finally {
      setChecking(false);
    }
  };

  return (
    <Box
      sx={{
        mx: { xs: -2, md: -3 }, // Bleed to screen edge (negate Layout's px)
        mb: 2,
        px: { xs: 2, md: 3 },
        py: 1.25,
        bgcolor: isDark
          ? alphaHex(iosColors.amber, 0.12)
          : alphaHex(iosColors.amber, 0.16),
        borderBottom: '1px solid',
        borderBottomColor: alphaHex(iosColors.amber, 0.3),
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: { xs: 'wrap', md: 'nowrap' },
      }}
      role="status"
      aria-live="polite"
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: alphaHex(iosColors.amber, 0.2),
          color: iosColors.amber,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <MarkEmailReadIcon sx={{ fontSize: 18 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.3,
          }}
        >
          Verify your email to secure your account
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            color: 'text.secondary',
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          We sent a link to <b>{currentUser.email}</b>. Click it to enable password recovery.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, alignItems: 'center' }}>
        <Button
          size="small"
          variant="text"
          startIcon={
            resendState === 'sent' ? (
              <CheckCircleIcon sx={{ fontSize: 16 }} />
            ) : (
              <SendIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={handleResend}
          disabled={resendState === 'sending'}
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            textTransform: 'none',
            color: iosColors.amber,
            minHeight: 32,
            px: 1.25,
            '&:hover': { bgcolor: alphaHex(iosColors.amber, 0.12) },
          }}
        >
          {resendState === 'sending'
            ? 'Sending…'
            : resendState === 'sent'
            ? 'Sent!'
            : resendState === 'error'
            ? 'Try again'
            : 'Resend'}
        </Button>

        <Button
          size="small"
          variant="text"
          startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
          onClick={handleCheck}
          disabled={checking}
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            textTransform: 'none',
            color: 'text.secondary',
            minHeight: 32,
            px: 1.25,
            '&:hover': { color: 'text.primary', bgcolor: alphaHex('#000000', 0.04) },
          }}
        >
          {checking ? 'Checking…' : "I've verified"}
        </Button>

        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label="Dismiss"
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default EmailVerificationBanner;
